import * as React from 'react';
import { Button } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';

import {
    PaginationView,
    AutocompleteField,
    Filters,
} from 'features/pagination/models/paginationModel';
import { AutocompleteSelect } from 'features/pagination/components/AutocompleteSelect';
import { FormFieldIds } from 'common/helpers/utils';
import { PaginationFilters } from 'features/pagination/reducers/paginationReducer';
import { PaginationRequestAction } from 'features/pagination/actions/paginationActions';
import { Antd3FormProps, WrappedFormUtils } from 'common/components/deprecated/antd3';
import { CreatePaths } from 'features/create/models/createModel';
import { TableFiltersBar } from 'common/components/TableFiltersBar';
import { FilterDrawerBenefits } from 'common/components/FilterDrawerBenefits';
import { GoldButton } from 'common/components/GoldButton';

import { EditPaths } from '../models/editModel';

type FormProps = {
    searchByBenefits?(benefitReferenceNumber: string): PaginationRequestAction;
    searchByContracts?(contractId: number): PaginationRequestAction;
    onRemoveBenefitAutocomplete?(): PaginationRequestAction;
    onRemoveContractAutocomplete?(): PaginationRequestAction;
    clearFilters?(): void;
    refreshTable?(): void;
    form: WrappedFormUtils;
    filter?: PaginationFilters;
};

type Props = FormProps & Antd3FormProps;

export const EditBenefitsTableFiltersBar: React.FunctionComponent<Props> = ({
    searchByBenefits,
    searchByContracts,
    onRemoveBenefitAutocomplete,
    onRemoveContractAutocomplete,
    clearFilters,
    refreshTable,
    form,
    filter,
}) => {
    const { pathname } = useLocation();
    const isMyView = pathname.includes(EditPaths.MyBenefits);
    const { referenceNumber, contract, ...filterSearchParams } = (filter && filter.values) || {};

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = React.useState(false);

    return (
        <>
            <TableFiltersBar
                leftButtons={[
                    <Link to={isMyView ? EditPaths.Benefits : EditPaths.MyBenefits}>
                        <Button type="primary">{isMyView ? 'All Benefits' : 'My Benefits'}</Button>
                    </Link>,
                    <Link to={CreatePaths.Benefit}>
                        <GoldButton icon={<PlusOutlined />}>New Benefit</GoldButton>
                    </Link>,
                ]}
                filters={[
                    <AutocompleteSelect
                        id={FormFieldIds.QuickFilterContractReferenceNumber}
                        placeholder="Search by Projects"
                        form={form}
                        autocompleteField={AutocompleteField.Contract}
                        onSelect={searchByContracts}
                        onRemove={onRemoveContractAutocomplete}
                        notFoundContent="No projects found"
                        requiredMessage="Project is required"
                        showLabel={false}
                        defaultValue={filter && filter.values.contract}
                        searchParams={filterSearchParams as Filters}
                    />,
                    <AutocompleteSelect
                        id={FormFieldIds.QuickFilterBenefitReferenceNumber}
                        placeholder="Search by Benefits"
                        form={form}
                        autocompleteField={
                            isMyView ? AutocompleteField.MyBenefit : AutocompleteField.Benefit
                        }
                        onSelect={searchByBenefits}
                        onRemove={onRemoveBenefitAutocomplete}
                        notFoundContent="No benefits found"
                        requiredMessage="Benefit is required"
                        showLabel={false}
                        defaultValue={filter && filter.values.referenceNumber}
                        searchParams={filterSearchParams as Filters}
                    />,
                ]}
                filtersButtons={[
                    <Button
                        type="primary"
                        icon={<FilterOutlined />}
                        onClick={() => setIsFilterDrawerOpen(true)}
                    >
                        All Filters
                    </Button>,
                    <Button onClick={clearFilters}>Clear Filters</Button>,
                ]}
            />
            <FilterDrawerBenefits
                visible={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}
                filterId={isMyView ? PaginationView.MyBenefits : PaginationView.Benefits}
                onApply={refreshTable}
            />
        </>
    );
};
